import { Link, useParams } from "react-router-dom";
import { Seo } from "@/components/site/Seo";
import { ArrowLeft, ArrowUpRight, MessageCircle, Phone } from "lucide-react";
import { FINISHES } from "@/data/finishes";
import { finishEnquiry, CALL_URL, PHONE_DISPLAY } from "@/data/company";
import { Reveal } from "@/components/site/Reveal";
import { FinishSpecs } from "@/components/site/FinishSpecs";
import NotFoundPage from "./NotFoundPage";

export default function FinishPage() {
  const { slug } = useParams();
  const finish = FINISHES.find((item) => item.id === slug);
  if (!finish) return <NotFoundPage />;
  const index = FINISHES.indexOf(finish);
  const related = [FINISHES[(index + 1) % FINISHES.length], FINISHES[(index + 2) % FINISHES.length]];

  return (
    <article key={finish.id} id="top" className="pt-28 md:pt-36 pb-24 px-6 md:px-12 lg:px-20" data-testid="finish-page">
      <Seo title={`${finish.name} — ${finish.finish} | Garg Stone Company, Kota`} description={`${finish.name} (${finish.finish}) from Garg Stone Company, Kota. ${finish.description}`.slice(0, 160)} path={`/finishes/${finish.id}`} />
      <Link to="/#products" className="inline-flex gap-2 items-center text-xs uppercase font-heading text-bone/60 transition-colors hover:text-copper" data-testid="finish-back-to-collection"><ArrowLeft size={15} /> The collection</Link>
      <Reveal className="mt-10 mb-10 md:mb-14">
        <p className="font-heading text-xs uppercase text-copper mb-4" data-testid="finish-eyebrow">Garg Stone Company / {finish.finish}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-[1.08]" data-testid="finish-title">{finish.name}</h1>
        <p className="text-sm md:text-base text-bone/65 leading-relaxed max-w-2xl mt-5" data-testid="finish-description">{finish.description}</p>
      </Reveal>
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <Reveal className="lg:col-span-7 min-w-0">
          <figure>
            <div className="aspect-[5/4] bg-stone overflow-hidden">
              <img src={finish.images[0].src} alt={`${finish.name} — ${finish.images[0].caption}`} className="w-full h-full object-contain" data-testid="finish-main-image" />
            </div>
            <figcaption className="text-xs text-bone/50 mt-3" data-testid="finish-main-caption">{finish.images[0].caption} · From our catalog</figcaption>
          </figure>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 min-w-0">
          <FinishSpecs finish={finish} />
          <div className="flex flex-col items-start gap-5 mt-8">
            <a href={finishEnquiry(finish)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-copper text-ink px-6 py-4 text-sm font-heading font-bold transition-colors hover:bg-bone" data-testid="finish-whatsapp-button"><MessageCircle size={18} className="shrink-0" /> Ask for a quote <ArrowUpRight size={16} /></a>
            <a href={CALL_URL} className="inline-flex gap-2 items-center text-sm text-bone/70 transition-colors hover:text-copper" data-testid="finish-call-button"><Phone size={15} /> {PHONE_DISPLAY}</a>
          </div>
        </Reveal>
      </div>
      <section className="mt-20 md:mt-28" data-testid="finish-photo-gallery">
        <Reveal><h2 className="font-heading text-lg mb-7" data-testid="finish-photos-heading">A closer look</h2></Reveal>
        <div className="grid sm:grid-cols-2 gap-6 md:gap-10">
          {finish.images.slice(1).map((image, i) => (
            <Reveal key={image.src} delay={i * 0.08}>
              <figure><div className="aspect-[4/3] bg-stone overflow-hidden"><img src={image.src} alt={`${finish.name} — ${image.caption}`} loading="lazy" className="h-full w-full object-contain" data-testid={`finish-detail-image-${i + 1}`} /></div><figcaption className="text-xs text-bone/60 mt-3" data-testid={`finish-detail-caption-${i + 1}`}>{image.caption}</figcaption></figure>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="mt-20 border-t border-bone/15 pt-9" data-testid="related-finishes">
        <h2 className="text-lg font-heading mb-5">Continue the collection</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {related.map((item) => <Link key={item.id} to={`/finishes/${item.id}`} data-testid={`related-finish-${item.id}`} className="group flex justify-between items-center gap-5 py-6 border-b border-bone/15 transition-colors hover:text-copper"><span><span className="block text-xs text-bone/50 mb-2">{item.finish}</span><span className="font-heading text-xl">{item.name}</span></span><ArrowUpRight size={22} className="shrink-0 transition-transform group-hover:translate-x-1" /></Link>)}
        </div>
      </section>
    </article>
  );
}